import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { jobsApi, matchesApi, companiesApi } from '../lib/api';
import { usePageVisitLogger, logJobView } from '../hooks/useActivityLogger';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { StatusBadge, PriorityBadge, SkillTag, ScoreBadge } from '../components/common/Badges';
import ScoreRing from '../components/common/ScoreRing';
import {
  ArrowLeft,
  MapPin,
  Briefcase,
  Building2,
  Clock,
  DollarSign,
  Users,
  Target,
  Loader2,
  CheckCircle,
  Calendar,
  Globe,
  Edit,
  Save,
  X,
  Zap,
  TrendingUp,
  Award,
  Phone,
  Mail,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Activity logging
  usePageVisitLogger('Job Detail');
  
  const [job, setJob] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Deal Closer state
  const [dealCloserCandidates, setDealCloserCandidates] = useState([]);
  const [dealCloserLoading, setDealCloserLoading] = useState(false);
  const [dealCloserExpanded, setDealCloserExpanded] = useState(null);

  useEffect(() => {
    loadJob();
    loadMatches();
    loadCompanies();
    loadDealCloser();
  }, [id]);

  const loadJob = async () => {
    try {
      const response = await jobsApi.get(id);
      setJob(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load job');
      navigate('/ats/jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await companiesApi.list({ limit: 100 });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Failed to load companies');
    }
  };

  // Company search function for type-ahead
  const searchCompanies = async (query) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies?search=${encodeURIComponent(query)}&limit=20`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` }
      });
      const data = await response.json();
      return (data.companies || []).map(c => ({ ...c, name: c.company_name || c.name }));
    } catch (error) {
      console.error('Failed to search companies:', error);
      return [];
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await jobsApi.update(id, editData);
      setJob(response.data);
      setIsEditing(false);
      toast.success('Job updated');
    } catch (error) {
      toast.error('Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  const loadMatches = async () => {
    try {
      const response = await matchesApi.list({ job_id: id, limit: 20 });
      setMatches(response.data.matches);
    } catch (error) {
      console.error('Failed to load matches:', error);
    }
  };

  const loadDealCloser = async () => {
    setDealCloserLoading(true);
    try {
      const response = await jobsApi.getDealCloser(id, { limit: 10 });
      setDealCloserCandidates(response.data.top_candidates || []);
    } catch (error) {
      console.error('Failed to load deal closer:', error);
    } finally {
      setDealCloserLoading(false);
    }
  };

  const refreshDealCloser = () => {
    loadDealCloser();
  };

  const formatSalary = (salary) => {
    if (!salary) return 'N/A';
    const s = String(salary).toLowerCase();
    if (s.includes('lpa') || s.includes('lakh')) return salary;
    const num = parseFloat(salary);
    if (isNaN(num)) return salary;
    if (num >= 100000) return `₹${(num/100000).toFixed(1)} LPA`;
    return `₹${num.toLocaleString()}`;
  };

  const formatMargin = (margin) => {
    if (!margin || margin === 0) return 'N/A';
    if (margin >= 100000) return `₹${(margin/100000).toFixed(1)}L`;
    return `₹${Math.round(margin).toLocaleString()}`;
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await jobsApi.update(id, { status: newStatus });
      setJob({ ...job, status: newStatus });
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!job) return null;

  const linkedCompany = companies.find(c => c.id === job.company_id);

  return (
    <div>
      <Header 
        title={job.job_title} 
        subtitle={linkedCompany?.name || job.company_name || job.job_code}
      >
        <Button variant="outline" onClick={() => navigate('/ats/jobs')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {isEditing ? (
          <>
            <Button variant="outline" onClick={() => { setEditData(job); setIsEditing(false); }}>
              <X className="w-4 h-4 mr-2" />Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-job-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Select value={job.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-32" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="filled">Filled</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </Header>

      <div className="page-container">
        {/* Editing Panel */}
        {isEditing && (
          <Card className="mb-6 border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle>Edit Job Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Number of Openings</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editData.number_of_openings || 1}
                    onChange={(e) => setEditData({ ...editData, number_of_openings: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <Label>Company <span className="text-xs text-slate-500">(Type 3+ chars)</span></Label>
                  <SearchableSelect
                    value={editData.company_id || ''}
                    onValueChange={(v) => setEditData({ ...editData, company_id: v || '' })}
                    onSearch={searchCompanies}
                    options={companies.map(c => ({ ...c, name: c.company_name || c.name }))}
                    placeholder="Type company name..."
                    minChars={3}
                    displayKey="name"
                    valueKey="id"
                    renderOption={(company) => (
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span>{company.name || company.company_name}</span>
                      </div>
                    )}
                  />
                </div>
                <div>
                  <Label>Job Title</Label>
                  <Input
                    value={editData.job_title || ''}
                    onChange={(e) => setEditData({ ...editData, job_title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={editData.location || ''}
                    onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Financial Details Section */}
              <div className="pt-4 border-t border-slate-200">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  Financial Details (for Margin Calculation)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Billing Rate (% of CTC)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="e.g., 8.33 for 1 month"
                      value={editData.billing_rate_percentage || ''}
                      onChange={(e) => setEditData({ ...editData, billing_rate_percentage: parseFloat(e.target.value) || null })}
                    />
                    <p className="text-xs text-slate-500 mt-1">8.33% = 1 month CTC</p>
                  </div>
                  <div>
                    <Label>OR Fixed Billing Cost (₹)</Label>
                    <Input
                      type="number"
                      step="1000"
                      min="0"
                      placeholder="e.g., 50000"
                      value={editData.billing_rate_cost || ''}
                      onChange={(e) => setEditData({ ...editData, billing_rate_cost: parseFloat(e.target.value) || null })}
                    />
                    <p className="text-xs text-slate-500 mt-1">Fixed fee per placement</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <Card data-testid="overview-card">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{job.job_title}</h2>
                    {job.company_name && (
                      <p className="text-slate-600 flex items-center gap-1 mt-1">
                        <Building2 className="w-4 h-4" />
                        {job.company_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={job.status} />
                    <PriorityBadge priority={job.priority_level} />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {job.primary_location && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {job.primary_location}
                    </div>
                  )}
                  {job.job_type && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Briefcase className="w-4 h-4 text-slate-400" />
                      {job.job_type}
                    </div>
                  )}
                  {job.work_type && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      {job.work_type}
                    </div>
                  )}
                  {(job.min_experience_years !== null || job.max_experience_years !== null) && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4 text-slate-400" />
                      {job.min_experience_years || 0}-{job.max_experience_years || '∞'} years
                    </div>
                  )}
                </div>

                {(job.salary_min || job.salary_max) && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    <span className="text-slate-600">
                      {job.salary_min} - {job.salary_max}
                      {job.salary_type && ` (${job.salary_type})`}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="w-full justify-start" data-testid="detail-tabs">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="requirements">Requirements</TabsTrigger>
                <TabsTrigger value="benefits">Benefits</TabsTrigger>
                <TabsTrigger value="candidates">Candidates ({matches.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="description" className="mt-4">
                <Card>
                  <CardContent className="p-6">
                    {job.job_description ? (
                      <div className="prose prose-sm max-w-none">
                        <div className="whitespace-pre-wrap text-slate-600">{job.job_description}</div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">No description available</p>
                    )}

                    {job.primary_responsibilities?.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-slate-100">
                        <h4 className="font-semibold text-slate-900 mb-3">Key Responsibilities</h4>
                        <ul className="space-y-2">
                          {job.primary_responsibilities.map((resp, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                              <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="requirements" className="mt-4">
                <Card>
                  <CardContent className="p-6 space-y-6">
                    {job.must_have_skills?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Must Have Skills</h4>
                        <div className="flex flex-wrap gap-1">
                          {job.must_have_skills.map((skill, i) => (
                            <SkillTag key={i} skill={skill} matched />
                          ))}
                        </div>
                      </div>
                    )}

                    {job.good_to_have_skills?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Good to Have</h4>
                        <div className="flex flex-wrap gap-1">
                          {job.good_to_have_skills.map((skill, i) => (
                            <span key={i} className="skill-tag">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {job.programming_languages_required?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Programming Languages</h4>
                        <div className="flex flex-wrap gap-1">
                          {job.programming_languages_required.map((lang, i) => (
                            <span key={i} className="skill-tag">{lang}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {job.certifications_required?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Required Certifications</h4>
                        <div className="flex flex-wrap gap-1">
                          {job.certifications_required.map((cert, i) => (
                            <span key={i} className="badge badge-info">{cert}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {job.min_education && (
                        <div>
                          <span className="text-slate-500">Minimum Education:</span>
                          <p className="font-medium">{job.min_education}</p>
                        </div>
                      )}
                      {job.english_proficiency && (
                        <div>
                          <span className="text-slate-500">English:</span>
                          <p className="font-medium">{job.english_proficiency}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="benefits" className="mt-4">
                <Card>
                  <CardContent className="p-6 space-y-4">
                    {job.benefits?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Benefits</h4>
                        <div className="flex flex-wrap gap-2">
                          {job.benefits.map((benefit, i) => (
                            <span key={i} className="badge badge-success">{benefit}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {job.perks?.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-3">Perks</h4>
                        <div className="flex flex-wrap gap-2">
                          {job.perks.map((perk, i) => (
                            <span key={i} className="badge badge-info">{perk}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {!job.benefits?.length && !job.perks?.length && (
                      <p className="text-sm text-slate-500 text-center py-8">No benefits information</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="candidates" className="mt-4">
                <Card>
                  <CardContent className="p-6">
                    {matches.length > 0 ? (
                      <div className="space-y-3">
                        {matches.map((match) => (
                          <Link
                            key={match.id}
                            to={`/ats/matches/${match.id}`}
                            className="flex items-center gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors border border-slate-100"
                            data-testid={`candidate-match-${match.id}`}
                          >
                            <ScoreRing score={match.overall_score} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900">
                                {match.candidate?.name || 'Unknown'}
                              </p>
                              <p className="text-sm text-slate-500">
                                {match.candidate?.current_designation || match.candidate?.email}
                              </p>
                            </div>
                            <div className="text-right">
                              <ScoreBadge score={match.overall_score} showLabel={false} />
                              <p className="text-xs text-slate-500 mt-1">{match.recommendation_level}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-8">No matching candidates yet</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card data-testid="info-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Job Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Code</span>
                  <span className="font-mono">{job.job_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Openings</span>
                  <span className="font-medium">{job.number_of_openings}</span>
                </div>
                {job.department && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Department</span>
                    <span>{job.department}</span>
                  </div>
                )}
                {job.reports_to && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Reports To</span>
                    <span>{job.reports_to}</span>
                  </div>
                )}
                {job.team_size && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Team Size</span>
                    <span>{job.team_size}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Interview Process */}
            {job.interview_process?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Interview Process</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {job.interview_process.map((step, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium">
                          {i + 1}
                        </span>
                        <span className="text-slate-600">{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            )}

            {/* AI Deal Closer Engine */}
            <Card data-testid="deal-closer-card" className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <Zap className="w-4 h-4 text-white" />
                    </div>
                    <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent font-bold">
                      Deal Closer
                    </span>
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={refreshDealCloser}
                    disabled={dealCloserLoading}
                    data-testid="refresh-deal-closer-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${dealCloserLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Top 10 candidates ranked by match, availability & revenue potential</p>
              </CardHeader>
              <CardContent className="pt-0">
                {dealCloserLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                  </div>
                ) : dealCloserCandidates.length > 0 ? (
                  <div className="space-y-2">
                    {dealCloserCandidates.map((candidate, index) => (
                      <div 
                        key={candidate.candidate_id}
                        className="group"
                        data-testid={`deal-closer-candidate-${index}`}
                      >
                        <div 
                          className={`p-3 rounded-lg cursor-pointer transition-all ${
                            dealCloserExpanded === index 
                              ? 'bg-purple-100 border border-purple-300' 
                              : 'hover:bg-purple-50 border border-transparent'
                          }`}
                          onClick={() => setDealCloserExpanded(dealCloserExpanded === index ? null : index)}
                        >
                          {/* Main Row */}
                          <div className="flex items-center gap-3">
                            {/* Rank Badge */}
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
                              index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white' :
                              index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-600 text-white' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {index + 1}
                            </div>
                            
                            {/* Name & Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{candidate.candidate_name}</p>
                              <p className="text-xs text-slate-500 truncate">
                                {candidate.current_designation || 'N/A'}
                              </p>
                            </div>
                            
                            {/* Scores */}
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <p className="text-sm font-bold text-purple-600">{candidate.ranking_score}%</p>
                                <p className="text-[10px] text-slate-400">Score</p>
                              </div>
                              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${dealCloserExpanded === index ? 'rotate-90' : ''}`} />
                            </div>
                          </div>

                          {/* Score Pills */}
                          <div className="flex gap-1.5 mt-2">
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 rounded">
                              {candidate.match_score}% Match
                            </span>
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded">
                              {candidate.placement_probability}% Prob
                            </span>
                            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded">
                              {formatMargin(candidate.margin_potential)}
                            </span>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {dealCloserExpanded === index && (
                          <div className="mt-2 p-3 bg-white rounded-lg border border-purple-200 space-y-3">
                            {/* Contact & Quick Actions */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 text-xs">
                                {candidate.email && (
                                  <a href={`mailto:${candidate.email}`} className="flex items-center gap-1 text-slate-600 hover:text-purple-600">
                                    <Mail className="w-3 h-3" />
                                    Email
                                  </a>
                                )}
                                {candidate.phone && (
                                  <a href={`tel:${candidate.phone}`} className="flex items-center gap-1 text-slate-600 hover:text-purple-600">
                                    <Phone className="w-3 h-3" />
                                    Call
                                  </a>
                                )}
                              </div>
                              <Link 
                                to={`/ats/candidates/${candidate.candidate_id}`}
                                className="text-xs font-medium text-purple-600 hover:text-purple-700"
                              >
                                View Profile →
                              </Link>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-slate-500">Experience:</span>
                                <span className="ml-1 font-medium">{candidate.total_years_experience || 'N/A'} yrs</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Location:</span>
                                <span className="ml-1 font-medium truncate">{candidate.current_location || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Expected:</span>
                                <span className="ml-1 font-medium">{formatSalary(candidate.expected_salary)}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Notice:</span>
                                <span className="ml-1 font-medium">{candidate.notice_period_days || 'N/A'} days</span>
                              </div>
                            </div>

                            {/* Score Breakdown */}
                            <div className="pt-2 border-t border-slate-100">
                              <p className="text-[10px] font-medium text-slate-500 mb-1.5">SCORE BREAKDOWN</p>
                              <div className="grid grid-cols-4 gap-1">
                                <div className="text-center p-1.5 bg-slate-50 rounded">
                                  <p className="text-xs font-bold text-slate-700">{candidate.skills_score}%</p>
                                  <p className="text-[9px] text-slate-500">Skills</p>
                                </div>
                                <div className="text-center p-1.5 bg-slate-50 rounded">
                                  <p className="text-xs font-bold text-slate-700">{candidate.experience_score}%</p>
                                  <p className="text-[9px] text-slate-500">Exp</p>
                                </div>
                                <div className="text-center p-1.5 bg-slate-50 rounded">
                                  <p className="text-xs font-bold text-slate-700">{candidate.location_score}%</p>
                                  <p className="text-[9px] text-slate-500">Location</p>
                                </div>
                                <div className="text-center p-1.5 bg-slate-50 rounded">
                                  <p className="text-xs font-bold text-slate-700">{candidate.compensation_score}%</p>
                                  <p className="text-[9px] text-slate-500">Comp</p>
                                </div>
                              </div>
                            </div>

                            {/* Matching Skills */}
                            {candidate.matching_skills?.length > 0 && (
                              <div>
                                <p className="text-[10px] font-medium text-slate-500 mb-1">MATCHING SKILLS</p>
                                <div className="flex flex-wrap gap-1">
                                  {candidate.matching_skills.slice(0, 6).map((skill, i) => (
                                    <span key={i} className="px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-700 rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Positive Factors */}
                            {candidate.positive_factors?.length > 0 && (
                              <div>
                                <p className="text-[10px] font-medium text-slate-500 mb-1">WHY GOOD FIT</p>
                                <div className="flex flex-wrap gap-1">
                                  {candidate.positive_factors.map((factor, i) => (
                                    <span key={i} className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-purple-50 text-purple-700 rounded">
                                      <Award className="w-2.5 h-2.5" />
                                      {factor}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No matching candidates found</p>
                    <p className="text-xs text-slate-400">Try adding more job requirements</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
